<template>
  <v-container fluid>
    <PageHeader :title="(isCreate ? 'Create' : 'Edit') + ' ACH'" icon="mdi-file-document-outline" />
    <div class="ma-4">
      <v-card class="elevation-1 card mt-4 pa-4 card-form">
        <LoadingProgress :loading="loading" />
        <v-form ref="form">
          <v-card-text>
            <v-text-field
              label="Bank Name"
              v-model="ach.bankName"
              counter
              persistent-counter
              :rules="[requiredRule]"
              :maxlength="255"
            ></v-text-field>
            <v-select
              v-model="ach.country"
              :items="countries"
              label="Country"
              item-title="name"
              item-value="code"
              :rules="[requiredRule]"
              @update:modelValue="onCountryChange"
            ></v-select>
            <v-text-field
              label="Routing Number"
              v-model="ach.routingNumber"
              counter
              persistent-counter
              :rules="[requiredRule, numericRule, routingNumberRule]"
              :maxlength="255"
            ></v-text-field>
            <v-text-field
              label="Account Number"
              v-model="ach.accountNumber"
              counter
              persistent-counter
              :rules="[requiredRule, numericRule]"
              :maxlength="255"
            ></v-text-field>
            <v-select
              label="Account Type"
              v-model="ach.accountTypeId"
              :items="accountTypes"
              item-title="name"
              item-value="id"
              :rules="[simpleRequiredRule]"
            ></v-select>
            <v-select
              label="Profile"
              v-model="orgUnitIds"
              :items="orgUnitOptions"
              item-title="name"
              item-value="id"
              multiple
              chips
              persistent-hint
              :rules="[requiredRule]"
            ></v-select>
            <v-select
              label="Connection"
              v-model="partyIndexes"
              :items="partyOptions"
              item-title="name"
              item-value="index"
              multiple
              chips
              persistent-hint
              :rules="[requiredRule]"
            ></v-select>
            <v-file-input
              v-model="attachments"
              label="Attachments (Optional)"
              multiple
              chips
              show-size
              counter
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
              :hint="attachmentHint"
              persistent-hint
              :clearable="true"
              :rules="[fileSizeRule, fileTypeRule]"
              color="primary"
              clear-icon="mdi-close"
              style="cursor: pointer"
            ></v-file-input>
          </v-card-text>
        </v-form>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="save()" :disabled="loading" size="large" color="primary" variant="flat">Save</v-btn>
          <v-btn @click="cancel()" :disabled="loading" size="large" variant="flat">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import Auth0Mixin from "../Auth0Mixin";
import LoadingProgress from "@/components/LoadingProgress.vue";
import PageHeader from "@/components/PageHeader.vue";
import orgUnitService from "../../services/admin/orgUnitService";
import AccountTypeList from "../../constants/accountTypeList";
import partyService from "../../services/partyService";
import ValidationRules from "../../validationRules";
import achService from "../../services/achService";

const MAX_TOTAL_FILE_SIZE_BYTES = 8388608; // 8 MB

export default {
  name: "Ach",
  mixins: [Auth0Mixin],
  components: { LoadingProgress, PageHeader },
  data() {
    return {
      ach: {
        bankName: null,
        routingNumber: null,
        accountNumber: null,
        accountTypeId: null,
        partiesByOrgUnitId: [],
      },
      accountTypes: AccountTypeList,
      orgUnitOptions: [],
      partyOptions: [],
      orgUnitIds: [],
      partyIndexes: [],
      attachments: [],
      requiredRule: ValidationRules.requiredRule,
      simpleRequiredRule: v => !!v || "Required",
      numericRule: ValidationRules.numeric,
      routingNumberRule: true,
      fileSizeRule: v => {
        if (!v || !v.length) return true;
        const totalSize = v.reduce((sum, f) => sum + f.size, 0);
        const maxSizeMB = MAX_TOTAL_FILE_SIZE_BYTES / (1024 * 1024);
        if (totalSize > MAX_TOTAL_FILE_SIZE_BYTES) return `Total file size should not exceed ${maxSizeMB} MB`;
        return true;
      },
      fileTypeRule: v => {
        if (!v || !v.length) return true;
        const allowedExtensions = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt", ".png", ".jpg", ".jpeg"];
        return (
          v.every(f => {
            const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
            return allowedExtensions.includes(ext);
          }) || "Only PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, and JPG files are allowed"
        );
      },
      isCreate: false,
      loading: false,
      countries: [
        { code: "US", name: "United States of America" },
        { code: "O", name: "Other" },
      ],
    };
  },
  computed: {
    attachmentHint() {
      const maxSizeMB = MAX_TOTAL_FILE_SIZE_BYTES / (1024 * 1024);
      return `Upload supporting documents (PDF, DOC, XLS, PNG, JPG, etc.). Max ${maxSizeMB} MB total.`;
    },
  },
  methods: {
    async init() {
      this.loading = true;

      const orgUnits = await orgUnitService.getList(true);
      this.orgUnitOptions = orgUnits.items
        .filter(orgUnit => orgUnit.isActive === true)
        .map(o => ({ id: o.id, name: o.name }));

      this.isCreate = this.$route.params.id === "create";

      if (!this.isCreate) {
        let selectedAch = await achService.get(this.$route.params.id);

        this.ach.id = selectedAch.id;
        this.ach.isActive = selectedAch.isActive;
        this.ach.bankName = selectedAch.bankName;
        this.ach.accountTypeId = selectedAch.accountTypeId;
        this.orgUnitIds = selectedAch.partiesByOrgUnitId.map(pair => pair.key);
        this.ach.country = selectedAch.country;

        this.onCountryChange();

        await this.populateParties(this.orgUnitIds);

        selectedAch.partiesByOrgUnitId.forEach(pair => {
          pair.value.forEach(party => {
            let foundParty = this.partyOptions.find(p => p.id == party.id && p.orgUnitId == pair.key);
            if (foundParty) this.partyIndexes.push(foundParty.index);
          });
        });
      }

      this.loading = false;
    },

    async populateParties(orgUnitIds) {
      if (orgUnitIds.length > 0) {
        let ids = orgUnitIds.join();
        let partiesGroupedByOrgUnitId = await partyService.getPartiesByOrgUnitId(ids);

        let selectedParties = this.partyOptions.filter(party => this.partyIndexes.includes(party.index));
        this.partyOptions = [];
        let index = 0;

        partiesGroupedByOrgUnitId.forEach(pair => {
          let orgUnit = this.orgUnitOptions.find(x => x.id == pair.key);
          let parties = pair.value;

          parties.forEach(party => {
            party.name += " (" + orgUnit.name + ")";
            party.orgUnitId = orgUnit.id;
            party.index = index;
            index++;
          });

          this.partyOptions.push(...parties);
        });

        if (this.partyIndexes.length > 0) {
          let filteredParties = this.partyOptions.filter(party =>
            selectedParties.some(sp => sp.id == party.id && sp.orgUnitId == party.orgUnitId)
          );
          this.partyIndexes = filteredParties.map(party => party.index);
        }
      } else {
        this.partyOptions = [];
      }
    },

    onCountryChange() {
      this.routingNumberRule = this.ach.country === "US" ? ValidationRules.usRoutingNumberRule : true;
    },

    async save() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      this.loading = true;

      this.ach.partiesByOrgUnitId = [];
      this.orgUnitIds.forEach(orgUnitId => {
        let parties = this.partyOptions.filter(
          party => party.orgUnitId == orgUnitId && this.partyIndexes.includes(party.index)
        );
        this.ach.partiesByOrgUnitId.push({ key: orgUnitId, value: parties });
      });

      try {
        if (this.isCreate) {
          await achService.create(this.ach, this.attachments);
          this.$root.$snackbar.message("ACH Saved");
        } else {
          await achService.update(this.ach, this.attachments);
          this.$root.$snackbar.message("ACH Updated");
        }
        this.$router.push("/admin/achs");
      } catch (err) {
        this.loading = false;
        this.$root.$snackbar.exception(err);
      }
    },

    cancel() {
      this.$router.push("/admin/achs");
    },
  },
  watch: {
    orgUnitIds: async function(orgUnitIds) {
      await this.populateParties(orgUnitIds);
    },
  },
};
</script>

<style scoped>
:deep(.v-file-input .v-file-input__text--placeholder),
:deep(.v-file-input .v-file-input__text) {
  cursor: pointer !important;
}
:deep(.v-file-input.v-input--focused .v-input__prepend .v-icon),
:deep(.v-file-input.v-input--focused .v-field__clearable .v-icon) {
  color: rgb(var(--v-theme-primary)) !important;
}
</style>
